import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useUtmTaxonomyStore } from '@/store/utm-taxonomy-store';
import UtmColorPicker from '@/components/utm/shared/UtmColorPicker';
import UtmIconPicker from '@/components/utm/shared/UtmIconPicker';
import { Save, X } from 'lucide-react';

const UtmCategoryForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { categories, createCategory, updateCategory, fetchTaxonomy } = useUtmTaxonomyStore();

  const isEditMode = Boolean(id);
  const existingCategory = categories.find((c) => c.id === id);

  const [formData, setFormData] = useState({
    name: '',
    defaultVisibleStatus: 'VISIBLE_WHEN_CHECKED' as 'ALWAYS_VISIBLE' | 'VISIBLE_WHEN_CHECKED',
    description: '',
    iconType: 'BUILTIN' as 'BUILTIN' | 'SVG' | 'IMAGE_URL' | 'S3_OBJECT',
    iconValue: 'shield',
    iconColor: '#ffffff',
    iconBgColor: '#1e293b',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    fetchTaxonomy();
  }, [fetchTaxonomy]);

  useEffect(() => {
    if (isEditMode && existingCategory) {
      setFormData({
        name: existingCategory.name,
        defaultVisibleStatus: existingCategory.defaultVisibleStatus || 'VISIBLE_WHEN_CHECKED',
        description: existingCategory.description || '',
        iconType: existingCategory.iconType,
        iconValue: existingCategory.iconValue,
        iconColor: existingCategory.iconColor || '#ffffff',
        iconBgColor: existingCategory.iconBgColor || '#1e293b',
      });
    }
  }, [isEditMode, existingCategory]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      if (isEditMode && id) {
        await updateCategory(id, formData);
      } else {
        await createCategory(formData);
      }
      navigate('/admin/uzamaps/categories');
    } catch (error) {
      console.error('Failed to save category:', error);
      alert('Failed to save category. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleIconChange = (iconType: string, iconValue: string) => {
    setFormData((prev) => ({
      ...prev,
      iconType: iconType as 'BUILTIN' | 'SVG' | 'IMAGE_URL' | 'S3_OBJECT',
      iconValue,
    }));
  };

  return (
    <div className="mx-auto max-w-4xl p-4 sm:p-6 lg:p-8">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-foreground sm:text-3xl">
          {isEditMode ? 'Edit Category' : 'Create Category'}
        </h1>
        <p className="text-muted-foreground mt-1">
          {isEditMode ? 'Update category details' : 'Add a new UTM category to organize zones'}
        </p>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Information */}
        <div className="bg-card border border-border rounded-lg p-4 sm:p-6">
          <h2 className="text-lg font-semibold text-foreground mb-4">Basic Information</h2>

          <div className="space-y-4">
            {/* Name */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-4 py-2 bg-muted border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-ring"
                placeholder="e.g., Restricted Zones"
                required
              />
            </div>

            {/* Default visible status */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Default visible status
              </label>
              <select
                value={formData.defaultVisibleStatus}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    defaultVisibleStatus: e.target.value as 'ALWAYS_VISIBLE' | 'VISIBLE_WHEN_CHECKED',
                  })
                }
                className="w-full px-4 py-2 bg-muted border border-border rounded-lg text-foreground focus:outline-none focus:border-ring"
              >
                <option value="ALWAYS_VISIBLE">Always visible</option>
                <option value="VISIBLE_WHEN_CHECKED">Visible when checked</option>
              </select>
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full px-4 py-2 bg-muted border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-ring"
                placeholder="Optional description"
                rows={3}
              />
            </div>
          </div>
        </div>

        {/* Icon Settings */}
        <div className="bg-card border border-border rounded-lg p-4 sm:p-6">
          <h2 className="text-lg font-semibold text-foreground mb-4">Icon Settings</h2>

          <div className="space-y-4">
            {/* Icon Picker */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Icon</label>
              <UtmIconPicker
                iconType={formData.iconType}
                iconValue={formData.iconValue}
                onChange={handleIconChange}
              />
            </div>

            {/* Colors */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Icon Color
                </label>
                <UtmColorPicker
                  color={formData.iconColor}
                  onChange={(color) => setFormData({ ...formData, iconColor: color })}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Background Color
                </label>
                <UtmColorPicker
                  color={formData.iconBgColor}
                  onChange={(color) => setFormData({ ...formData, iconBgColor: color })}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-end sm:space-x-0">
          <button
            type="button"
            onClick={() => navigate('/admin/uzamaps/categories')}
            className="flex w-full items-center justify-center space-x-2 rounded-lg bg-muted px-4 py-2 text-foreground transition-colors hover:bg-accent sm:w-auto"
          >
            <X size={18} />
            <span>Cancel</span>
          </button>
          <button
            type="submit"
            disabled={isSubmitting || !formData.name}
            className="flex w-full items-center justify-center space-x-2 rounded-lg bg-primary px-4 py-2 text-primary-foreground transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:bg-muted sm:w-auto"
          >
            <Save size={18} />
            <span>{isSubmitting ? 'Saving...' : 'Save Category'}</span>
          </button>
        </div>
      </form>
    </div>
  );
};

export default UtmCategoryForm;
